import type { FC, ComponentType, ReactNode } from 'react';
import classnames from 'classnames';
import { CssTransition } from 'sento-transition';
import type { FieldProps, FormikErrors } from 'formik';

import { InputLabel } from 'app/components/Text';

import style from './style.scss';

type FieldStatus = 'UNTOUCHED' | 'VALID' | 'INVALID';

interface Props extends FieldProps {
  label: string;
  validMessage?: string;
  errorMessage?: string;
  fieldComponent: ComponentType<any>;
  className?: string;
  labelClassName?: string;
}

interface FieldMessageProps {
  isError: boolean;
  children: ReactNode;
  className?: string;
}

const MESSAGE_CLASSNAMES = {
  enter: style.belowMessage_enter,
  enterActive: style.belowMessage_enterActive,
  exit: style.belowMessage_exit,
  exitActive: style.belowMessage_exitActive
};

function getStatus(
  value: any,
  touched: boolean,
  error?: string | FormikErrors<any> | string[] | FormikErrors<any>[]
): FieldStatus {
  if (touched && error) {
    return 'INVALID';
  }
  if (!touched || !value) {
    return 'UNTOUCHED';
  }
  return 'VALID';
}

const FieldMessage = ({ isError, children, className }: FieldMessageProps) => {
  const messageClassName = classnames(style.belowMessage, className, {
    [style.belowMessage_error]: isError
  });

  return <div className={messageClassName}>{children}</div>;
};

const FormField: FC<Props> = ({
  field,
  form,
  label,
  fieldComponent: Component,
  validMessage,
  errorMessage,
  className,
  labelClassName,
  ...props
}) => {
  const { name, value } = field;
  const touched = !!form.touched[name];
  const error = errorMessage || form.errors[name];
  const status = getStatus(value, touched, error);
  const hasErrorMessage = touched && !!error;
  const hasValidMessage = touched && !!value && !!validMessage;
  const hasMessage = hasErrorMessage || hasValidMessage;

  return (
    <div className={classnames(style.container, className)}>
      {label && <InputLabel className={labelClassName}>{label}</InputLabel>}
      <div className={style.fieldContainer}>
        <Component status={status} className={style.fieldComponent} {...field} {...props} />
      </div>
      <CssTransition displayed={hasMessage} classNames={MESSAGE_CLASSNAMES} timeout={200}>
        <FieldMessage isError={hasErrorMessage}>
          <>{error || validMessage}</>
        </FieldMessage>
      </CssTransition>
    </div>
  );
};

FormField.displayName = 'PandaField';

export default FormField;
export type { FieldStatus };
